import { useQuery } from '@tanstack/react-query';
import { toIsoInstant, type ReservationSummary } from '@swi/contracts';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as api from '../api/endpoints';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  EmptyState,
  Pill,
  Skeleton,
  STATE_LABEL,
  stateColor,
} from '../components/ui/primitives';
import { dateOnly, dateTime, duration, money, weekday } from '../lib/format';

const DAY = 24 * 3_600_000;

/**
 * Dispatch board: one row per airship, time along the x axis.
 *
 * Reservation blocks are coloured by state, maintenance is hatched, and the
 * mandatory ground turnaround is drawn semi-transparent on both sides of every
 * blocking reservation — so the operator can see at a glance why a gap is not
 * actually bookable.
 */
export function DispatchPage() {
  const [days, setDays] = useState(7);
  const [anchor, setAnchor] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start;
  });

  const from = anchor;
  const to = new Date(anchor.getTime() + days * DAY);

  const airships = useQuery({ queryKey: ['airships', 'dispatch'], queryFn: () => api.listAirships() });

  const reservations = useQuery({
    queryKey: ['reservations', 'dispatch', from.toISOString(), days],
    queryFn: () =>
      api.listReservations({
        from: toIsoInstant(from),
        to: toIsoInstant(to),
        pageSize: 200,
      }),
  });

  const byAirship = useMemo(() => {
    const map = new Map<number, ReservationSummary[]>();
    for (const reservation of reservations.data?.items ?? []) {
      const list = map.get(reservation.airshipId) ?? [];
      list.push(reservation);
      map.set(reservation.airshipId, list);
    }
    return map;
  }, [reservations.data]);

  const span = to.getTime() - from.getTime();
  const position = (iso: string): number =>
    ((new Date(iso).getTime() - from.getTime()) / span) * 100;

  const dayTicks = Array.from({ length: days }, (_, index) => new Date(from.getTime() + index * DAY));

  return (
    <>
      <PageHeader
        title="Dispatch board"
        description="Řádek = vzducholoď, sloupec = čas. Poloprůhledně povinná pozemní obsluha, šrafovaně servis."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAnchor(new Date(anchor.getTime() - days * DAY))}
            >
              ← {String(days)} d
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                setAnchor(today);
              }}
            >
              Dnes
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAnchor(new Date(anchor.getTime() + days * DAY))}
            >
              {String(days)} d →
            </Button>
            {[3, 7, 14].map((option) => (
              <Button
                key={option}
                variant={days === option ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setDays(option)}
              >
                {option} dní
              </Button>
            ))}
          </>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2 text-[11px]">
        {(
          [
            'DRAFT',
            'PENDING_APPROVAL',
            'CONFIRMED',
            'IN_FLIGHT',
            'COMPLETED',
            'CANCELLED',
            'REJECTED',
          ] as const
        ).map((state) => (
          <span key={state} className="inline-flex items-center gap-1.5 text-[var(--text-muted)]">
            <span
              aria-hidden
              className="inline-block size-2.5 rounded-[3px]"
              style={{ background: stateColor(state) }}
            />
            {STATE_LABEL[state]}
          </span>
        ))}
        <Pill tone="warn">pozemní obsluha</Pill>
      </div>

      <Card className="overflow-x-auto p-0">
        {airships.isLoading || reservations.isLoading ? (
          <Skeleton className="m-5 h-72" />
        ) : (
          <div className="min-w-[900px] p-4">
            {/* Day ruler */}
            <div className="mb-2 flex items-end gap-3">
              <span className="w-40 shrink-0" />
              <div className="relative h-8 flex-1 border-b border-[var(--border-subtle)]">
                {dayTicks.map((day, index) => (
                  <span
                    key={day.getTime()}
                    className="absolute bottom-1 text-[11px] text-[var(--text-secondary)]"
                    style={{ left: `${String((index / days) * 100)}%` }}
                  >
                    <span className="mono">{weekday(day.toISOString())}</span>{' '}
                    {dateOnly(day.toISOString())}
                  </span>
                ))}
              </div>
            </div>

            <ul className="flex flex-col gap-1.5">
              {airships.data?.map((airship) => {
                const rows = byAirship.get(airship.id) ?? [];
                return (
                  <li key={airship.id} className="flex items-center gap-3">
                    <Link
                      to={`/fleet/${String(airship.id)}`}
                      className="w-40 shrink-0 hover:underline"
                    >
                      <span className="mono block text-[12px] font-semibold text-[var(--accent)]">
                        {airship.registration}
                      </span>
                      <span className="block truncate text-[11px] text-[var(--text-muted)]">
                        {airship.name} · {airship.turnaroundMinutes} min
                      </span>
                    </Link>

                    <div className="relative h-10 flex-1 overflow-hidden rounded-[var(--radius-sm)] border border-[var(--border-subtle)] bg-[var(--surface-glass)]">
                      {dayTicks.map((day, index) => (
                        <span
                          key={day.getTime()}
                          aria-hidden
                          className="absolute inset-y-0 w-px bg-[var(--border-subtle)]"
                          style={{ left: `${String((index / days) * 100)}%` }}
                        />
                      ))}

                      {airship.status !== 'ACTIVE' && (
                        <span
                          aria-hidden
                          className="absolute inset-0"
                          style={{
                            backgroundImage:
                              'repeating-linear-gradient(45deg, var(--danger-dim) 0 6px, transparent 6px 12px)',
                          }}
                        />
                      )}

                      {rows.map((reservation) => {
                        const left = position(reservation.startAt);
                        const right = position(reservation.endAt);
                        const width = Math.max(right - left, 0.5);
                        const blocking =
                          reservation.state === 'CONFIRMED' || reservation.state === 'IN_FLIGHT';
                        const bufferPct =
                          (reservation.turnaroundMinutes * 60_000 / span) * 100;

                        return (
                          <div key={reservation.id}>
                            {blocking && bufferPct > 0 && (
                              <>
                                <span
                                  aria-hidden
                                  className="absolute inset-y-0"
                                  style={{
                                    left: `${String(left - bufferPct)}%`,
                                    width: `${String(bufferPct)}%`,
                                    background: 'var(--warn)',
                                    opacity: 0.22,
                                  }}
                                />
                                <span
                                  aria-hidden
                                  className="absolute inset-y-0"
                                  style={{
                                    left: `${String(right)}%`,
                                    width: `${String(bufferPct)}%`,
                                    background: 'var(--warn)',
                                    opacity: 0.22,
                                  }}
                                />
                              </>
                            )}
                            <Link
                              to={`/reservations/${reservation.id}`}
                              title={`${STATE_LABEL[reservation.state]} · ${reservation.departureHubCode} → ${reservation.arrivalHubCode}
${dateTime(reservation.startAt)} → ${dateTime(reservation.endAt)} (${duration(reservation.startAt, reservation.endAt)})
${reservation.renterName} · ${reservation.passengers} pax · ${money(reservation.priceCzk)}`}
                              className="absolute inset-y-1 flex items-center overflow-hidden rounded-[4px] px-1.5 transition-[filter] hover:brightness-125"
                              style={{
                                left: `${String(Math.max(left, 0))}%`,
                                width: `${String(width)}%`,
                                background: stateColor(reservation.state),
                                opacity: reservation.state === 'CANCELLED' ? 0.35 : 0.9,
                              }}
                            >
                              <span
                                className="mono truncate text-[10px] font-semibold"
                                style={{ color: 'var(--surface-void)' }}
                              >
                                {reservation.departureHubCode}→{reservation.arrivalHubCode}
                              </span>
                            </Link>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>

            {(reservations.data?.items.length ?? 0) === 0 && (
              <div className="mt-4">
                <EmptyState title="V tomto období nejsou žádné rezervace" />
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
